import React, { useEffect, useMemo, useState } from "react";
import { prettifyStoreName, fmtKr, ALL_STORES } from "./workshopCapacityUtils";
import { PaymentDetailModal } from "./WorkshopSalgsrapportTab";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const CUSTOMER_PORTAL_BASE = "https://status.roykensykkelservice.no";

function fmtDate(d) {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "—";
}

function MarkerSendtModal({ row, onClose, onDone }) {
  const [sporingsnummer, setSporingsnummer] = useState("");
  const [sendtAv, setSendtAv] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setSporingsnummer(row?.sporingsnummer || "");
    setSendtAv("");
    setError("");
  }, [row?.id, row?.sporingsnummer]);

  if (!row) return null;

  async function handleConfirm() {
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${row.id}/sending/marker-sendt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sporingsnummer, sendtAv }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(json?.message || "Kunne ikke markere som sendt");
        return;
      }
      onDone(row.id, json.sending);
    } catch (e) {
      setError("Kunne ikke markere som sendt");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="text-lg font-semibold mb-1">Marker som sendt</div>
        <div className="text-sm text-gray-500 mb-4">Service #{row.serviceRef} — {row.customerName}</div>

        <div className="space-y-2 text-sm">
          <div className="p-3 bg-gray-50 rounded border">
            <div>{row.adresse?.mottakerNavn}</div>
            <div>{row.adresse?.gate}</div>
            <div>{row.adresse?.postnr} {row.adresse?.poststed}</div>
          </div>

          <label className="block text-xs text-gray-600 mt-3">Sporingsnummer (valgfritt)</label>
          <input
            type="text"
            value={sporingsnummer}
            onChange={(e) => setSporingsnummer(e.target.value)}
            className="w-full border rounded px-2 py-1.5"
          />
          <p className="text-xs text-gray-400">Kunden varsles automatisk med sporingslenke (e-post, evt. SMS) dersom du fyller inn dette.</p>

          <label className="block text-xs text-gray-600 mt-2">Sendt av (valgfritt)</label>
          <input
            type="text"
            value={sendtAv}
            onChange={(e) => setSendtAv(e.target.value)}
            className="w-full border rounded px-2 py-1.5"
          />
        </div>

        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleConfirm}
            disabled={busy}
            className="px-4 py-2 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-60"
          >
            {busy ? "Registrerer…" : "Bekreft sendt"}
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded border text-sm hover:bg-gray-50">
            Avbryt
          </button>
        </div>
      </div>
    </div>
  );
}

function postnordSporingUrl(sporingsnummer) {
  return `https://www.postnord.no/pakkesporing/?shipmentId=${encodeURIComponent(sporingsnummer)}`;
}

function SporingsnummerCell({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.sporingsnummer || "");
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${row.id}/sending/sporingsnummer`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sporingsnummer: value }),
      });
      if (r.ok) {
        onSaved(row.id, value);
        setEditing(false);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        {row.sporingsnummer ? (
          <a
            href={postnordSporingUrl(row.sporingsnummer)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs text-blue-700 hover:underline"
            title="Åpne sporing hos PostNord"
          >
            {row.sporingsnummer}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        )}
        <button onClick={() => setEditing(true)} className="text-xs text-blue-600 hover:underline shrink-0">
          {row.sporingsnummer ? "Endre" : "Legg til"}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="border rounded px-1.5 py-0.5 text-xs w-28"
        autoFocus
      />
      <button onClick={save} disabled={busy} className="text-xs px-1.5 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
        {busy ? "…" : "Lagre"}
      </button>
      <button
        onClick={() => { setEditing(false); setValue(row.sporingsnummer || ""); }}
        className="text-xs px-1.5 py-0.5 rounded border hover:bg-gray-50"
      >
        Avbryt
      </button>
    </div>
  );
}

export default function WorkshopSendingerTab() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [storeSlug, setStoreSlug] = useState("alle");
  const [status, setStatus] = useState("venter"); // venter | sendt | alle
  const [selectedRow, setSelectedRow] = useState(null);
  const [receiptRow, setReceiptRow] = useState(null);
  const [receiptLoadingId, setReceiptLoadingId] = useState(null);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API_BASE_URL}/workshop/sendinger?verkstedSlug=${encodeURIComponent(storeSlug)}&status=${status}`
        );
        const data = r.ok ? await r.json() : { rows: [] };
        setRows(data.rows || []);
      } catch (err) {
        console.error("Feil ved henting av sendinger:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [storeSlug, status]);

  const antallUtenAdresse = useMemo(
    () => rows.filter((r) => !r.adresse?.mottakerNavn).length,
    [rows]
  );

  async function handleAngreSendt(row) {
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${row.id}/sending/angre-sendt`, {
        method: "POST",
      });
      if (!r.ok) return;
      setRows((prev) => prev.filter((x) => x.id !== row.id));
    } catch (e) {
      console.error(e);
    }
  }

  function handleMarkertSendt(rowId) {
    setRows((prev) => prev.filter((r) => r.id !== rowId));
    setSelectedRow(null);
  }

  function handleSporingsnummerSaved(rowId, sporingsnummer) {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, sporingsnummer } : r)));
  }

  async function openReceipt(id) {
    setReceiptLoadingId(id);
    try {
      const r = await fetch(`${API_BASE_URL}/workshop/payments/${id}`);
      const data = r.ok ? await r.json() : null;
      if (data?.row) setReceiptRow(data.row);
    } catch (e) {
      console.error("Feil ved henting av kvittering:", e);
    } finally {
      setReceiptLoadingId(null);
    }
  }

  function handleReceiptRefunded(rowId, payment) {
    setReceiptRow((prev) =>
      prev && prev.id === rowId
        ? { ...prev, status: payment.status, refundertBelop: payment.refundertBelop }
        : prev
    );
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, betalingsstatus: payment.status } : r)));
  }

  if (loading) return <div className="py-10 text-center">Laster sendinger…</div>;

  return (
    <div className="space-y-6">
      <MarkerSendtModal row={selectedRow} onClose={() => setSelectedRow(null)} onDone={handleMarkertSendt} />
      <PaymentDetailModal row={receiptRow} onClose={() => setReceiptRow(null)} onRefunded={handleReceiptRefunded} />

      <div className="rounded border bg-green-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-sm text-gray-600">Sendinger</div>
            <div className="text-xl font-bold">
              {status === "venter" ? "Venter på sending" : status === "sendt" ? "Sendt" : "Alle"}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={storeSlug}
              onChange={(e) => setStoreSlug(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="alle">Alle verksteder</option>
              {ALL_STORES.map((slug) => (
                <option key={slug} value={slug}>{prettifyStoreName(slug)}</option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="venter">Venter på sending</option>
              <option value="sendt">Sendt</option>
              <option value="alle">Alle</option>
            </select>
          </div>
        </div>
      </div>

      {status === "venter" && antallUtenAdresse > 0 && (
        <div className="rounded border border-orange-300 bg-orange-50 p-3 text-sm text-orange-800">
          {antallUtenAdresse} betalt{antallUtenAdresse === 1 ? "" : "e"} service{antallUtenAdresse === 1 ? "" : "r"} med frakt mangler fortsatt leveringsadresse fra kunden.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full border rounded bg-white text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left">Ordre</th>
              <th className="px-3 py-2 text-left">Kunde</th>
              <th className="px-3 py-2 text-left">Produkt</th>
              <th className="px-3 py-2 text-left">Adresse</th>
              <th className="px-3 py-2 text-left">Frakt</th>
              <th className="px-3 py-2 text-right">Beløp</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Sendingsnr.</th>
              <th className="px-3 py-2 text-left">Varsel</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 font-mono">
                  <div>{r.serviceRef}</div>
                  <div className="mt-1 flex flex-wrap gap-2 font-sans">
                    <button
                      onClick={() => openReceipt(r.id)}
                      disabled={receiptLoadingId === r.id}
                      className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                    >
                      {receiptLoadingId === r.id ? "Henter…" : "Kvittering"}
                    </button>
                    {r.portalToken && (
                      <a
                        href={`${CUSTOMER_PORTAL_BASE}/svc/${r.portalToken}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Kundeportal
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div>{r.customerName}</div>
                  {r.customerPhone && <div className="text-xs text-gray-500">{r.customerPhone}</div>}
                  {r.customerEmail && <div className="text-xs text-gray-500">{r.customerEmail}</div>}
                </td>
                <td className="px-3 py-2">{r.produktnavn || "—"}</td>
                <td className="px-3 py-2">
                  {r.adresse?.mottakerNavn ? (
                    <>
                      <div>{r.adresse.mottakerNavn}</div>
                      <div className="text-xs text-gray-500">{r.adresse.gate}, {r.adresse.postnr} {r.adresse.poststed}</div>
                    </>
                  ) : (
                    <span className="text-orange-600 text-xs">Mangler adresse</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {r.fraktLines.map((f, i) => (
                    <div key={i}>{f.beskrivelse}</div>
                  ))}
                </td>
                <td className="px-3 py-2 text-right">{fmtKr(r.vippsBelop)}</td>
                <td className="px-3 py-2">
                  {r.sendtDato ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-semibold">
                      Sendt {fmtDate(r.sendtDato)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 text-xs font-semibold">
                      Venter
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <SporingsnummerCell row={r} onSaved={handleSporingsnummerSaved} />
                </td>
                <td className="px-3 py-2">
                  {r.sporingsvarsel ? (
                    <span
                      className="inline-flex items-center gap-1 text-xs text-emerald-700"
                      title={`Sendt til ${r.sporingsvarsel.til}`}
                    >
                      {r.sporingsvarsel.kanal === "epost" ? "✉️" : "📱"} {fmtDate(r.sporingsvarsel.sendtDato)}
                    </span>
                  ) : r.sporingsnummer ? (
                    <span className="text-xs text-gray-400">Ikke sendt</span>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.sendtDato ? (
                    <button
                      onClick={() => handleAngreSendt(r)}
                      className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                    >
                      Angre
                    </button>
                  ) : (
                    <button
                      onClick={() => setSelectedRow(r)}
                      disabled={!r.adresse?.mottakerNavn}
                      className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={!r.adresse?.mottakerNavn ? "Venter på leveringsadresse fra kunden" : ""}
                    >
                      Marker som sendt
                    </button>
                  )}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-gray-500">
                  Ingen sendinger {status === "venter" ? "venter" : status === "sendt" ? "er sendt" : "funnet"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
